/**
 * Stage Drawer — full-screen overlay showing stage detail.
 *
 * Opened when user clicks a phase river node. Contains tabs:
 * Summary, Tasks (conditional), Prompt, Events, Config.
 *
 * CSS: styles/components/drawer.css
 */

import { esc } from '../../shared/rendering.js'
import { action } from './action-delegation.js'
import { renderTaskList } from './taskList.js'
// Honeycomb icon imports — will be re-enabled when StageDetail includes URLs
// import { iconTrace, iconDashboard, iconLogs } from './icons.js'
import { formatStageName } from '../../types/domain.js'
import type { DrawerProps, TaskListProps } from '../../types/components.js'

/** Render the drawer overlay shell. Content is populated by updateDrawer(). */
export function renderDrawerShell(): string {
  return `<div class="drawer-overlay" id="drawer-overlay" ${action('closeDrawer')}>
  <div class="drawer">
    <div class="dr-head">
      <div class="dr-head-top">
        <span class="dr-title" id="dr-title">Stage</span>
        <button class="dr-close" ${action('closeDrawer')}>&times;</button>
      </div>
      <div style="display:flex;align-items:center;gap:12px;margin-top:4px">
        <div class="dr-meta" id="dr-meta"></div>
        <div style="display:flex;gap:4px;margin-left:auto" id="dr-links"></div>
      </div>
    </div>
    <div class="dr-tabs" id="dr-tabs"></div>
    <div class="dr-body" id="dr-body"></div>
  </div>
</div>`
}

/** Update the drawer with stage detail data. */
export function updateDrawer(props: DrawerProps): void {
  const titleEl = document.getElementById('dr-title')
  const metaEl = document.getElementById('dr-meta')
  const linksEl = document.getElementById('dr-links')
  const tabsEl = document.getElementById('dr-tabs')
  const bodyEl = document.getElementById('dr-body')
  if (!titleEl || !metaEl || !linksEl || !tabsEl || !bodyEl) {
    return
  }

  // Title
  const label = formatStageName(props.stage)
  const statusIcon =
    props.status === 'done' ? '&#10003;' : props.status === 'failed' ? '&#10007;' : '&#9679;'
  const statusColor =
    props.status === 'done' ? 'var(--grn)' : props.status === 'failed' ? 'var(--err)' : 'var(--cyn)'
  titleEl.innerHTML = `<span style="color:${statusColor}">${statusIcon}</span> ${esc(label)}`

  // Meta
  const metaParts: string[] = [`<span>Stage: <strong>${esc(label)}</strong></span>`]
  if (props.loopNumber !== null) {
    metaParts.push(`<span>Loop: <strong>${props.loopNumber}</strong></span>`)
  }
  if (props.turns !== null) {
    metaParts.push(`<span>Turns: <strong>${props.turns}</strong></span>`)
  }
  if (props.duration) {
    metaParts.push(`<span>Duration: <strong>${esc(props.duration)}</strong></span>`)
  }
  if (props.tokens) {
    metaParts.push(`<span>Tokens: <strong>${esc(props.tokens)}</strong></span>`)
  }
  if (props.model) {
    metaParts.push(`<span>Model: <strong>${esc(props.model)}</strong></span>`)
  }
  metaEl.innerHTML = metaParts.join('')

  // Honeycomb links — only render when backend provides actual URLs
  // Currently drawer only has traceId, not constructed URLs.
  // Will be wired when StageDetail includes honeycomb_trace_url etc.
  linksEl.innerHTML = ''

  // Tabs
  const hasTasks = props.taskLists !== null && props.taskLists.length > 0
  const hasToolDist =
    props.toolDistribution !== null &&
    props.toolDistribution !== undefined &&
    props.toolDistribution.length > 0
  const tabs = [
    { id: 'dr-summary', label: 'Summary', show: true },
    { id: 'dr-tasks', label: 'Tasks', show: hasTasks },
    { id: 'dr-tools', label: 'Tool Distribution', show: hasToolDist },
    { id: 'dr-prompt', label: 'Prompt', show: props.prompt !== null },
    { id: 'dr-events', label: 'Events', show: true },
    { id: 'dr-cfg', label: 'Config', show: true },
  ].filter((t) => t.show)

  tabsEl.innerHTML = tabs
    .map(
      (t, i) =>
        `<button class="dr-tab${i === 0 ? ' active' : ''}" data-dtab="${esc(t.id)}" ${action('switchDrawerTab', t.id)}>${esc(t.label)}</button>`,
    )
    .join('')

  // Tab content
  const summaryHtml = renderSummaryTab(props)
  const tasksHtml = hasTasks && props.taskLists ? renderTasksTab(props.taskLists) : ''
  const toolsHtml = hasToolDist ? renderToolsTab(props.toolDistribution ?? []) : ''
  const promptHtml =
    props.prompt !== null
      ? `<div class="sl">System Prompt</div><div class="dr-prompt">${esc(props.prompt)}</div>`
      : ''
  const eventsHtml =
    '<div class="sl">Stage Events</div><div style="font-size:12px;color:var(--t3)">Filtered event stream for this stage.</div>'
  const configHtml =
    '<div class="sl">Stage Config</div><div style="font-size:12px;color:var(--t3)">Configuration will be loaded here.</div>'

  bodyEl.innerHTML = `
    <div class="dr-tc active" id="dr-summary">${summaryHtml}</div>
    ${hasTasks ? `<div class="dr-tc" id="dr-tasks">${tasksHtml}</div>` : ''}
    ${hasToolDist ? `<div class="dr-tc" id="dr-tools">${toolsHtml}</div>` : ''}
    ${props.prompt !== null ? `<div class="dr-tc" id="dr-prompt">${promptHtml}</div>` : ''}
    <div class="dr-tc" id="dr-events">${eventsHtml}</div>
    <div class="dr-tc" id="dr-cfg">${configHtml}</div>
  `
}

function renderSummaryTab(props: DrawerProps): string {
  const dur = props.duration ?? ''
  const tokens = props.tokens ?? ''
  const statusBg =
    props.status === 'done'
      ? 'var(--grn-d)'
      : props.status === 'failed'
        ? 'var(--err-d)'
        : 'var(--cyn-d)'
  const statusBd =
    props.status === 'done'
      ? 'rgba(62,207,160,.15)'
      : props.status === 'failed'
        ? 'rgba(248,113,113,.15)'
        : 'rgba(56,189,248,.15)'
  const statusColor =
    props.status === 'done' ? 'var(--grn)' : props.status === 'failed' ? 'var(--err)' : 'var(--cyn)'
  const statusText =
    props.status === 'done'
      ? 'Stage Completed'
      : props.status === 'failed'
        ? 'Stage Failed'
        : 'Stage Running'

  let html = `<div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;padding:10px 12px;background:${statusBg};border:1px solid ${statusBd};border-radius:8px">
    <div><div style="font-size:12px;font-weight:600;color:${statusColor}">${statusText}</div><div style="font-size:11px;color:var(--t2)">${esc(dur)}${dur && tokens ? ' \u00b7 ' : ''}${esc(tokens)}</div></div>
  </div>`

  if (props.responseSummary) {
    html += `<div class="sl">Assistant</div>
    <div style="font-size:12px;color:var(--t2);line-height:1.6;margin-bottom:6px">${esc(props.responseSummary)}</div>`
  }

  if (props.filesTouched.length > 0) {
    html += '<div class="sl">Files Touched</div>'
    html += props.filesTouched
      .map(
        (f) =>
          `<div class="si"><span style="font-size:10px;color:${f.op === 'create' ? 'var(--grn)' : 'var(--t3)'};min-width:12px">${f.op === 'create' ? '+' : '~'}</span><span class="si-file">${esc(f.path)}</span></div>`,
      )
      .join('')
  }

  if (props.kbItemsCreated.length > 0) {
    html += '<div class="sl">KB Items Created</div>'
    html += props.kbItemsCreated
      .map(
        (k) =>
          `<div class="si"><span class="si-badge ${esc(k.type)}">${esc(k.type)}</span><span class="si-txt" style="flex:1">${esc(k.title)}</span></div>`,
      )
      .join('')
  }

  return html
}

function renderTasksTab(taskLists: readonly TaskListProps[]): string {
  return `<div class="sl">Task Lists</div>${taskLists.map((tl) => renderTaskList(tl)).join('')}`
}

const FILL_COLORS = ['pf1', 'pf2', 'pf3', 'pf4']

function renderToolsTab(
  tools: readonly {
    label: string
    totalCalls: number
    totalDuration: string
    percent: number
  }[],
): string {
  const rows = tools
    .map((t, i) => {
      const fillClass = FILL_COLORS[i % FILL_COLORS.length]
      return `<div style="margin-bottom:8px" title="${esc(t.label)}: ${t.percent}% of stage latency · ${t.totalCalls} calls · total ${esc(t.totalDuration)}">
      <div style="display:flex;align-items:baseline;gap:8px;font-size:10px;margin-bottom:2px">
        <span style="color:var(--t2)">${esc(t.label)}</span>
        <span style="font-family:var(--mono);font-size:9px;color:var(--t2);margin-left:auto">${esc(t.totalDuration)} &middot; ${t.totalCalls} calls</span>
      </div>
      <div class="pbar" title="${esc(t.label)}: ${t.percent}%"><div class="pfill ${fillClass}" style="width:${Math.min(100, t.percent)}%" title="${esc(t.label)} &middot; ${t.percent}% &middot; ${esc(t.totalDuration)}"></div></div>
      <div style="font-family:var(--mono);font-size:8px;color:var(--t4);margin-top:1px">${t.percent}% &middot; total ${esc(t.totalDuration)}</div>
    </div>`
    })
    .join('')

  const tableRows = tools
    .map(
      (t) =>
        `<tr><td style="padding:3px 8px;font-family:var(--mono);font-size:10px">${esc(t.label)}</td>
        <td style="padding:3px 8px;text-align:right;font-family:var(--mono);font-size:10px">${t.totalCalls}</td>
        <td style="padding:3px 8px;text-align:right;font-family:var(--mono);font-size:10px">${esc(t.totalDuration)}</td>
        <td style="padding:3px 8px;text-align:right;font-family:var(--mono);font-size:10px">${t.percent}%</td></tr>`,
    )
    .join('')

  const detailTable = `<table style="width:100%;border-collapse:collapse;margin-top:8px">
    <thead><tr style="border-bottom:1px solid var(--bd)">
      <th style="padding:3px 8px;text-align:left;font-size:9px;color:var(--t4);font-weight:500">Tool</th>
      <th style="padding:3px 8px;text-align:right;font-size:9px;color:var(--t4);font-weight:500">Calls</th>
      <th style="padding:3px 8px;text-align:right;font-size:9px;color:var(--t4);font-weight:500">Total Latency</th>
      <th style="padding:3px 8px;text-align:right;font-size:9px;color:var(--t4);font-weight:500">% Total</th>
    </tr></thead>
    <tbody>${tableRows}</tbody>
  </table>`

  return `<div class="sl">Tool Breakdown</div>${rows}
  <div style="margin-top:12px;border-top:1px solid var(--bd);padding-top:8px">
    <div style="font-size:9px;color:var(--t4);margin-bottom:4px">FULL BREAKDOWN</div>
    ${detailTable}
  </div>`
}

/** Open the drawer overlay. */
export function openDrawerOverlay(): void {
  document.getElementById('drawer-overlay')?.classList.add('open')
}

/** Close the drawer overlay. */
export function closeDrawer(): void {
  document.getElementById('drawer-overlay')?.classList.remove('open')
}

/** Switch drawer tabs. Exposed globally for onclick. */
export function switchDrawerTab(btn: HTMLElement): void {
  const tabsContainer = btn.closest('.dr-tabs')
  const bodyContainer = btn.closest('.drawer')?.querySelector('.dr-body')
  if (!tabsContainer || !bodyContainer) {
    return
  }
  tabsContainer.querySelectorAll('.dr-tab').forEach((t) => {
    t.classList.remove('active')
  })
  bodyContainer.querySelectorAll('.dr-tc').forEach((c) => {
    c.classList.remove('active')
  })
  btn.classList.add('active')
  const targetId = btn.getAttribute('data-dtab')
  if (targetId) {
    bodyContainer.querySelector(`#${targetId}`)?.classList.add('active')
  }
}
